import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import type { Client } from '@/api/clients/clientsApi.types';
import { Icon } from '@/components/common/Icon';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import { useDownloadClientDataExportMutation } from '@/hooks/useClientDataExport';
import { useAnonymiseClientMutation } from '@/hooks/useClientMutations';
import { formatIsoDateTimeToTimestamp } from '@/utils/dateFormatting';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';
import { AnonymiseClientDialog } from './AnonymiseClientDialog';

interface ClientPrivacyPanelProps {
  client: Client;
}

export function ClientPrivacyPanel({ client }: ClientPrivacyPanelProps) {
  const { t } = useTranslation('clients');
  const { activeWorkspace, isElevatedWorkspaceAccess } = useAuth();
  const downloadMutation = useDownloadClientDataExportMutation();
  const anonymiseMutation = useAnonymiseClientMutation();
  const [isAnonymiseOpen, setIsAnonymiseOpen] = useState(false);
  const isOwner =
    (activeWorkspace !== null && activeWorkspace.isOwner === true) || isElevatedWorkspaceAccess;
  const anonymisedAt = client.anonymisedAt;

  function downloadClientData() {
    downloadMutation.mutate(client.id, {
      onError: function reportDownloadError() {
        showErrorNotification(t('privacy.downloadError'));
      },
    });
  }

  function anonymise(confirmation: string) {
    anonymiseMutation.mutate(
      { clientId: client.id, confirmation },
      {
        onSuccess: function reportAnonymised() {
          setIsAnonymiseOpen(false);
          showSuccessNotification(t('privacy.anonymised'));
        },
        onError: function reportAnonymiseError(anonymiseError) {
          const backendMessage = resolveApiErrorMessage(anonymiseError);
          showErrorNotification(
            backendMessage.length > 0 ? backendMessage : t('privacy.anonymiseError')
          );
        },
      }
    );
  }

  return (
    <Card as="section">
      <div className="flex items-center gap-2 mb-2">
        <span className="w-9 h-9 rounded-xl bg-zoovpalelavender text-zooviolet flex items-center justify-center shrink-0">
          <Icon name="Shield" />
        </span>
        <h2 className="text-sm font-bold text-zooindigo">{t('privacy.title')}</h2>
      </div>
      <p className="text-xs text-zoomuted mb-3">{t('privacy.downloadDescription')}</p>
      <Button
        variant="outline"
        size="chip"
        isLoading={downloadMutation.isPending}
        onClick={downloadClientData}
      >
        <Icon name="Download" /> {t('privacy.download')}
      </Button>

      {anonymisedAt !== null ? (
        <p className="text-xs text-zoomuted mt-4 pt-4 border-t border-zooborder">
          {t('privacy.anonymisedOn', { date: formatIsoDateTimeToTimestamp(anonymisedAt) })}
        </p>
      ) : isOwner ? (
        <div className="mt-4 pt-4 border-t border-zooborder">
          <p className="text-xs text-zoomuted mb-3">{t('privacy.anonymiseDescription')}</p>
          <Button
            variant="destructiveGhost"
            size="chip"
            className="border border-zooborder"
            onClick={function openAnonymise() {
              setIsAnonymiseOpen(true);
            }}
          >
            <Icon name="ShieldAlert" /> {t('privacy.anonymiseAction')}
          </Button>
        </div>
      ) : null}

      {isAnonymiseOpen ? (
        <AnonymiseClientDialog
          clientName={client.fullName}
          isSubmitting={anonymiseMutation.isPending}
          onConfirm={anonymise}
          onCancel={function closeAnonymise() {
            setIsAnonymiseOpen(false);
          }}
        />
      ) : null}
    </Card>
  );
}
